import React, { useEffect } from "react";
import { Route, Switch, useLocation } from "react-router-dom";
import routes from "routes/index";
import privateRoutes from "routes/private.index";
import PublicRoute from "routes/PublicRoute";
import NotFound from "components/notFound/NotFound";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "App.scss";
import PrivateRoute from "routes/AuthRoute";
import { useDispatch, useSelector } from "react-redux";
import ImageViewer from "components/imageViewer/ImageViewer";
import { closeImageViewer } from "store/reducers/imageViewer.reducer";
import { RootState } from "store/combineReducers";
import { broadCastStorage } from "./helpers/utils";
import { setSelectedBroadCast } from "./store/actions/conversation.actions";
import { init } from "@plausible-analytics/tracker";
import FloatingHomeButton from "components/floatingHomeButton/FloatingHomeButton";
import PortraitModeGuard from "components/portraitModeGuard/PortraitModeGuard";
import JoinRequestNotificationListener from "components/joinRequestNotifications/JoinRequestNotificationListener";
import AdminMessageNotificationListener from "components/adminMessageNotifications/AdminMessageNotificationListener";
import { identifyUser, trackRouteChange } from "utils/clarity";
import { startDeployVersionCheck } from "utils/deployVersionCheck";
import { isAuthenticated } from "utils/authUtils";
import { dateTimeSettingRequest } from "store/actions/general.actions";
import { useDateTimeFormat } from "hooks/useDateTimeFormat";
import SEOMeta from "components/common/SEOMeta";
import { buildOrganizationSchema } from "utils/seoSchemas";
import { getSiteBaseUrl } from "utils/seoCanonical";

function App() {
  const dispatch = useDispatch();
  const imageViewer = useSelector(
    (s: RootState) => (s as any).imageViewer || { isOpen: false }
  );
  const location = useLocation();
  const isBroadcast = window.location.pathname.startsWith("/jitsi");
  // Keep module cache in sync for getCurrentDate / non-React helpers
  useDateTimeFormat();

  useEffect(() => {
    const broadCast = broadCastStorage.get();
    if (broadCast) {
      dispatch(setSelectedBroadCast(broadCast));
    }
  }, []);

  // Load date/time prefs whenever a session already exists (refresh / reopen)
  useEffect(() => {
    if (isAuthenticated()) {
      dispatch(dateTimeSettingRequest());
    }
  }, [dispatch]);

  useEffect(() => {
    // @ts-ignore
    window.process = { ...window.process };
  }, []);

  // Init Plausible Analytics
  useEffect(() => {
    init({
      domain: "moderatepolitics.org",
      autoCapturePageviews: true,
    });
  }, []);

  // Identify Existing User on App Load
  useEffect(() => {
    try {
      const userString = localStorage.getItem("user");
      if (userString) {
        const user = JSON.parse(userString);
        if (user && user.pk) {
          identifyUser(String(user.pk), user.email, {
            user_role: user.role !== undefined ? String(user.role) : undefined,
          });
        }
      }
    } catch (e) {
      console.error("Failed to initialize Clarity user tracking", e);
    }
  }, []);

  // Track route changes
  useEffect(() => {
    trackRouteChange(location.pathname);
  }, [location.pathname]);

  // Notify when a new frontend deploy is live (polls /version.json from CI)
  useEffect(() => {
    startDeployVersionCheck(() => {
      toast.info("New version available — Refresh", {
        toastId: "deploy-version-update",
        autoClose: false,
        closeOnClick: false,
        onClick: () => window.location.reload(),
        style: { cursor: "pointer" },
      });
    });
  }, []);

  const isMarketingHome =
    location.pathname === "/" || location.pathname === "/home";
  const organizationSchema = isMarketingHome
    ? JSON.stringify(buildOrganizationSchema(getSiteBaseUrl()))
    : undefined;

  return (
    <div className="App">
      <SEOMeta schema={organizationSchema} />
      <PortraitModeGuard />
      <JoinRequestNotificationListener />
      <AdminMessageNotificationListener />
      <div>
        {!isBroadcast && (
          <Switch>
            {privateRoutes &&
              privateRoutes.map((route, key) => (
                <PrivateRoute
                  key={key}
                  path={route.path}
                  component={route.component}
                />
              ))}
            {routes &&
              routes.map((route, key) => (
                <PublicRoute
                  key={key}
                  path={route.path}
                  component={route.component}
                />
              ))}
            <Route component={NotFound} />
          </Switch>
        )}
      </div>
      <FloatingHomeButton />
      <ToastContainer hideProgressBar />
      {imageViewer.isOpen && imageViewer.items?.length > 0 && (
        <ImageViewer
          items={imageViewer.items}
          index={imageViewer.index}
          onClose={() => dispatch(closeImageViewer())}
        />
      )}
    </div>
  );
}

export default App;
